import { useEffect, useMemo, useState } from 'react';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import HubIcon from '@mui/icons-material/Hub';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import {
  Autocomplete,
  Avatar,
  Button,
  Card,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Skeleton,
  CircularProgress,
  IconButton,
  Alert,
  MenuItem,
  Tab,
  Tabs,
} from '@mui/material';
import { useOrganizationSelect } from '../../context/OrganizationSelectContext';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrganizations } from '../../features/Organization/OrganizationSlice';

const ROOT_PRIORITY = ['TOOLBOX', 'MAYBOX', 'CLOUDBOX'];
const ROOT_TREE_SECTIONS = ['TOOLBOX', 'CLOUDBOX', 'MAYBOX'];
const ORGANIZATION_TYPE_META = {
  client: { label: 'Client', color: 'primary' },
  fournisseur: { label: 'Fournisseur', color: 'warning' },
  partenaire: { label: 'Partenaire', color: 'secondary' },
};

function normalizeRoots(organization) {
  if (Array.isArray(organization?.rootOrganizations) && organization.rootOrganizations.length > 0) {
    return organization.rootOrganizations
      .filter((root) => root?._id)
      .map((root) => ({
        _id: String(root._id),
        name: root.name || 'Sans nom',
      }));
  }

  if (organization?.rootOrganization?._id) {
    return [{
      _id: String(organization.rootOrganization._id),
      name: organization.rootOrganization.name || 'Sans nom',
    }];
  }

  return [];
}

function hasRoot(organization, rootId) {
  return normalizeRoots(organization).some((root) => String(root._id) === String(rootId));
}

function getPrimaryRoot(organization) {
  return normalizeRoots(organization)[0] || null;
}

function getRootNames(organization) {
  return normalizeRoots(organization).map((root) => root.name);
}

function normalizeOrganizationTypes(organization) {
  return [...new Set(
    (organization?.organizationTypes || [])
      .filter(Boolean)
      .map((type) => String(type).toLowerCase())
  )];
}

function getOrganizationTypeLabels(organization) {
  return normalizeOrganizationTypes(organization).map((type) =>
    ORGANIZATION_TYPE_META[type]?.label || type
  );
}

function getStaffLabel(user = {}) {
  const fullName = `${user.firstname || ''} ${user.lastname || ''}`.trim();
  return fullName || user.email || 'Staff';
}

function getStaffAvatarLabel(user = {}) {
  const first = String(user.firstname || '').trim();
  const last = String(user.lastname || '').trim();
  if (first || last) return `${first.slice(0, 1)}${last.slice(0, 2)}`.toUpperCase();
  return String(user.email || '?').slice(0, 3).toUpperCase();
}

const PORTFOLIO_ACCOUNT_COLORS = [
  { bgcolor: '#0f766e', color: '#fff', borderColor: '#0f766e' },
  { bgcolor: '#2563eb', color: '#fff', borderColor: '#2563eb' },
  { bgcolor: '#7c3aed', color: '#fff', borderColor: '#7c3aed' },
  { bgcolor: '#b45309', color: '#fff', borderColor: '#b45309' },
  { bgcolor: '#334155', color: '#fff', borderColor: '#334155' },
];

function StaffPortfolioAvatar({ entry }) {
  const user = entry.userDetails;
  const palette = PORTFOLIO_ACCOUNT_COLORS[(Number(entry.priority || 1) - 1) % PORTFOLIO_ACCOUNT_COLORS.length];

  return (
    <Tooltip title={`${entry.priority}. ${getStaffLabel(user || {})}`}>
      <Stack direction="row" spacing={0.45} alignItems="center" sx={{ flexShrink: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 900, lineHeight: 1, color: 'text.primary' }}>
          {entry.priority}.
        </Typography>
        <Avatar
          sx={{
            width: 24,
            height: 24,
            fontSize: '0.72rem',
            fontWeight: 900,
            border: '1px solid',
            ...palette,
          }}
        >
          {getStaffAvatarLabel(user || {})}
        </Avatar>
      </Stack>
    </Tooltip>
  );
}

function normalizeStaffPortfolio(portfolio = []) {
  return (Array.isArray(portfolio) ? portfolio : [])
    .map((entry, index) => ({
      user: entry.user?._id || entry.user || entry.userId,
      userDetails: entry.user,
      priority: Number(entry.priority || index + 1),
    }))
    .filter((entry) => entry.user)
    .sort((a, b) => a.priority - b.priority)
    .map((entry, index) => ({ ...entry, priority: index + 1 }));
}

function getOrganizationDisplayPortfolio(organization = {}) {
  const directPortfolio = normalizeStaffPortfolio(organization.staffPortfolio);
  if (directPortfolio.length > 0) return organization.staffPortfolio;

  const detailedItems = Array.isArray(organization.staffPortfolioItems)
    ? organization.staffPortfolioItems
    : [];
  const firstDetailedPortfolio = detailedItems.find((item) => normalizeStaffPortfolio(item.staffPortfolio).length > 0);
  return firstDetailedPortfolio?.staffPortfolio || [];
}

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}



const ADMINISTRATIVE_DEFAULTS = {
  paymentMode: '',
  paymentFrequency: '',
  m365Provider: '',
  m365Backup: '',
  m365LicenseBilling: '',
  esetLicenseBilling: '',
  o365LicenseType: '',
};

const ADMINISTRATIVE_SELECTS = {
  paymentMode: ['VIR', 'PREL CRT', 'PREL TOUTES LES FACTURES', 'CHQ', 'AUTRE'],
  paymentFrequency: ['Mensuelle', 'Annuelle'],
  m365Provider: ['MC3CLOUD', 'MC3CLOUDMYT', 'ARROW', 'EUCSP', 'PAS DE TENANT', 'AUTRE'],
  m365Backup: ['OUI', 'NON', 'OUI que les non nominatives'],
  m365LicenseBilling: ['Mensuelle', 'Annuelle'],
  esetLicenseBilling: ['Mensuelle', 'Annuelle'],
  o365LicenseType: ['Corporate', 'Education'],
};

const ADMINISTRATIVE_LABELS = {
  paymentMode: 'Mode de règlement',
  paymentFrequency: 'Fréquence de règlement',
  m365Provider: 'Fournisseur M365',
  m365Backup: 'Backup M365',
  m365LicenseBilling: 'Facturation LIC M365',
  esetLicenseBilling: 'Facturation LIC ESET',
  o365LicenseType: 'Type licences O365',
};

function getContractTitle(contract = {}) {
  return contract.reference || contract.name || 'Contrat sans référence';
}

function getContractSubtitle(contract = {}) {
  return [contract.name, contract.service, contract.contractType].filter(Boolean).join(' · ');
}

function formatDateTime(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function sortRoots(a, b) {
  const aName = (a?.name || '').toUpperCase();
  const bName = (b?.name || '').toUpperCase();
  const aIndex = ROOT_PRIORITY.indexOf(aName);
  const bIndex = ROOT_PRIORITY.indexOf(bName);

  if (aIndex !== -1 || bIndex !== -1) {
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  }

  return (a?.name || '').localeCompare(b?.name || '');
}

function buildTree(list) {
  const rootMap = new Map();
  const rootChildrenMap = new Map();
  const nodeMap = new Map();

  list.forEach((org) => {
    nodeMap.set(String(org._id), { ...org, children: [], relatedNodes: [] });
  });

  list.forEach((org) => {
    const node = nodeMap.get(String(org._id));
    const parentId = org?.parentOrganization?._id
      ? String(org.parentOrganization._id)
      : org?.organizationParent
        ? String(org.organizationParent)
        : null;

    if (parentId && nodeMap.has(parentId) && parentId !== String(org._id)) {
      nodeMap.get(parentId).children.push(node);
      return;
    }

    normalizeRoots(org).forEach((root) => {
      const rootId = String(root._id);

      if (!rootMap.has(rootId)) {
        rootMap.set(rootId, {
          _id: rootId,
          name: root.name,
          isVirtualRoot: true,
          children: [],
          relatedNodes: [],
          rootOrganizations: [root],
        });
        rootChildrenMap.set(rootId, new Map());
      }

      if (String(org._id) === rootId) {
        const realNode = nodeMap.get(String(org._id));
        const rootNode = rootMap.get(rootId);

        rootMap.set(rootId, {
          ...realNode,
          _id: rootId,
          name: realNode?.name || rootNode.name,
          isVirtualRoot: true,
          rootOrganizations: rootNode.rootOrganizations || [root],
          relatedNodes: realNode?.relatedNodes || [],
          children: realNode?.children || [],
        });
        return;
      }

      if (!parentId || !nodeMap.has(parentId) || !hasRoot(nodeMap.get(parentId), rootId)) {
        rootChildrenMap.get(rootId).set(String(org._id), node);
      }
    });
  });

  list.forEach((org) => {
    if (!org.organizationRelated) return;
    const relatedId = String(org.organizationRelated);
    if (nodeMap.has(relatedId)) {
      nodeMap.get(relatedId).relatedNodes.push(nodeMap.get(String(org._id)));
    }
  });

  rootMap.forEach((rootNode, rootId) => {
    const fallbackChildren = Array.from(rootChildrenMap.get(rootId)?.values() || []);

    rootNode.children = (rootNode.children && rootNode.children.length > 0
      ? rootNode.children
      : fallbackChildren
    ).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  });

  return Array.from(rootMap.values()).sort(sortRoots);
}

function renderRootsChips(organization, keyPrefix = 'root') {
  const roots = normalizeRoots(organization);

  if (roots.length === 0) {
    return <Typography variant="body2" color="text.secondary">Aucune</Typography>;
  }

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {roots.map((root, index) => (
        <Chip
          key={`${keyPrefix}-${organization?._id || root._id}-${root._id}`}
          size="small"
          color="primary"
          variant={index === 0 ? 'filled' : 'outlined'}
          label={root.name}
          sx={index === 0 ? { fontWeight: 700 } : undefined}
        />
      ))}
    </Stack>
  );
}

function renderTypeChips(organization, keyPrefix = 'type') {
  const types = normalizeOrganizationTypes(organization);

  if (types.length === 0) {
    return <Typography variant="body2" color="text.secondary">Aucun</Typography>;
  }

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {types.map((type) => {
        const meta = ORGANIZATION_TYPE_META[type] || { label: type, color: 'default' };
        return (
          <Chip
            key={`${keyPrefix}-${organization?._id || 'org'}-${type}`}
            size="small"
            color={meta.color}
            variant="outlined"
            label={meta.label}
          />
        );
      })}
    </Stack>
  );
}

const Organizations = () => {
  const dispatch = useDispatch();
  const [filteredOrganizations, setFilteredOrganizations] = useState([]);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingRefreshFromPipeHudu, setLoadingRefreshFromPipeHudu] = useState(false);
  const [organizationSyncStatus, setOrganizationSyncStatus] = useState(null);
  const [organizationSyncLoading, setOrganizationSyncLoading] = useState(false);
  const [organizationSyncError, setOrganizationSyncError] = useState('');
  const [treeData, setTreeData] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [portfolioDialogOpen, setPortfolioDialogOpen] = useState(false);
  const [portfolioSaving, setPortfolioSaving] = useState(false);
  const [editingPortfolioOrg, setEditingPortfolioOrg] = useState(null);
  const [portfolioDraftUsers, setPortfolioDraftUsers] = useState([]);
  const [portfolioGroupDialogOpen, setPortfolioGroupDialogOpen] = useState(false);
  const [portfolioGroupSaving, setPortfolioGroupSaving] = useState(false);
  const [editingPortfolioGroupOrg, setEditingPortfolioGroupOrg] = useState(null);
  const [portfolioGroupDraft, setPortfolioGroupDraft] = useState('');
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [detailsTab, setDetailsTab] = useState('administrative');
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailOrganization, setDetailOrganization] = useState(null);
  const [detailContracts, setDetailContracts] = useState([]);
  const [administrativeDraft, setAdministrativeDraft] = useState(ADMINISTRATIVE_DEFAULTS);

  const { data: organizations = [] } = useSelector((state) => state.organizations.organizations);
  const { selectedOrganization } = useOrganizationSelect();

  const staffOptions = useMemo(
    () => staffUsers.map((user) => ({ ...user, value: String(user._id), label: getStaffLabel(user) })),
    [staffUsers]
  );

  const staffById = useMemo(() => {
    const map = new Map();
    staffUsers.forEach((user) => map.set(String(user._id), user));
    return map;
  }, [staffUsers]);

  useEffect(() => {
    if (selectedOrganization) {
      setFilteredOrganizations(
        organizations.filter((item) => item.name === selectedOrganization)
      );
      return;
    }

    setFilteredOrganizations(organizations);
  }, [selectedOrganization, organizations]);

  useEffect(() => {
    if (filteredOrganizations && filteredOrganizations.length > 0) {
      setTreeData(buildTree(filteredOrganizations));
      return;
    }

    setTreeData([]);
  }, [filteredOrganizations]);

  useEffect(() => {
    const fetchStaffUsers = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/staff-portfolio/staff-options`,
          { headers: getAuthHeaders() }
        );
        setStaffUsers(Array.isArray(data) ? data : []);
      } catch (error) {
        setStaffUsers([]);
      }
    };

    fetchStaffUsers();
  }, []);

  const loadOrganizationSyncStatus = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/organizations/syncAll/status`);
      setOrganizationSyncStatus(data);
      setOrganizationSyncError('');
      return data;
    } catch (error) {
      setOrganizationSyncError(error.response?.data?.message || 'Impossible de récupérer le statut de synchronisation.');
      return null;
    }
  };

  const startOrganizationSync = async () => {
    try {
      setOrganizationSyncLoading(true);
      setOrganizationSyncError('');
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/organizations/syncAll`);
      setOrganizationSyncStatus(data?.status || null);
      setAlert({
        type: 'success',
        message: data?.message || 'Synchronisation lancée. Cette opération peut prendre plusieurs minutes.',
      });
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Impossible de lancer la synchronisation.';
      setOrganizationSyncError(message);
      setAlert({ type: 'error', message });
    } finally {
      setOrganizationSyncLoading(false);
      loadOrganizationSyncStatus();
    }
  };

  useEffect(() => {
    loadOrganizationSyncStatus();
  }, []);

  useEffect(() => {
    if (!organizationSyncStatus?.running) return undefined;
    const interval = setInterval(loadOrganizationSyncStatus, 5000);
    return () => clearInterval(interval);
  }, [organizationSyncStatus?.running]);


  const createHudufromDb = async (organizationId, organizationName) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/organizations/createHuduFromDB/${organizationId}`
      );

      if (response.status === 200 || response.status === 201) {
        dispatch(fetchOrganizations());
        setAlert({
          type: 'success',
          message: `Création dans Hudu réussie pour l'organisation: ${organizationName}`,
        });
      }
    } catch (error) {
      console.error('Error creating Hudu from DB:', error);
      setAlert({
        type: 'error',
        message: `Erreur lors de la création Hudu pour ${organizationName}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStaffPortfolio = (portfolio = []) => {
    const rows = normalizeStaffPortfolio(portfolio);
    if (!rows.length) return <Typography variant="body2" color="text.secondary">Aucun</Typography>;

    return (
      <Stack direction="row" spacing={1.1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ py: 0.35, minWidth: 0 }}>
        {rows.map((entry) => {
          const user = entry.userDetails || staffById.get(String(entry.user));
          return (
            <StaffPortfolioAvatar
              key={`${entry.user}-${entry.priority}`}
              entry={{ ...entry, userDetails: user }}
            />
          );
        })}
      </Stack>
    );
  };

  const openPortfolioDialog = (organization) => {
    const selectedUsers = normalizeStaffPortfolio(organization.staffPortfolio)
      .map((entry) => staffOptions.find((option) => option.value === String(entry.user)))
      .filter(Boolean);
    setEditingPortfolioOrg(organization);
    setPortfolioDraftUsers(selectedUsers);
    setPortfolioDialogOpen(true);
  };

  const closePortfolioDialog = () => {
    setPortfolioDialogOpen(false);
    setEditingPortfolioOrg(null);
    setPortfolioDraftUsers([]);
  };

  const savePortfolio = async () => {
    if (!editingPortfolioOrg?._id) return;
    setPortfolioSaving(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/staff-portfolio/organizations/${editingPortfolioOrg._id}`,
        {
          staffPortfolio: portfolioDraftUsers.map((user, index) => ({
            user: user.value || user._id,
            priority: index + 1,
          })),
        },
        { headers: getAuthHeaders() }
      );
      closePortfolioDialog();
      dispatch(fetchOrganizations());
      setAlert({
        type: 'success',
        message: `Portefeuille mis à jour pour ${editingPortfolioOrg.name}`,
      });
    } catch (error) {
      setAlert({
        type: 'error',
        message: error?.response?.data?.message || `Erreur lors de la mise à jour du portefeuille pour ${editingPortfolioOrg.name}`,
      });
    } finally {
      setPortfolioSaving(false);
    }
  };


  const openPortfolioGroupDialog = (organization) => {
    setEditingPortfolioGroupOrg(organization);
    setPortfolioGroupDraft(organization?.portfolioGroup || '');
    setPortfolioGroupDialogOpen(true);
  };

  const closePortfolioGroupDialog = () => {
    setPortfolioGroupDialogOpen(false);
    setEditingPortfolioGroupOrg(null);
    setPortfolioGroupDraft('');
  };

  const savePortfolioGroup = async () => {
    if (!editingPortfolioGroupOrg?._id) return;
    setPortfolioGroupSaving(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/organizations/${editingPortfolioGroupOrg._id}/portfolio-group`,
        { portfolioGroup: portfolioGroupDraft },
        { headers: getAuthHeaders() }
      );
      closePortfolioGroupDialog();
      dispatch(fetchOrganizations());
      setAlert({
        type: 'success',
        message: `Groupe mis à jour pour ${editingPortfolioGroupOrg.name}`,
      });
    } catch (error) {
      setAlert({
        type: 'error',
        message: error?.response?.data?.message || `Erreur lors de la mise à jour du groupe pour ${editingPortfolioGroupOrg.name}`,
      });
    } finally {
      setPortfolioGroupSaving(false);
    }
  };

  const openOrganizationDetails = async (organization) => {
    setDetailsDialogOpen(true);
    setDetailsTab('administrative');
    setDetailOrganization(organization);
    setDetailContracts([]);
    setAdministrativeDraft({ ...ADMINISTRATIVE_DEFAULTS, ...(organization?.administrative || {}) });
    setDetailsLoading(true);

    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/organizations/${organization._id}/details`,
        { headers: getAuthHeaders() }
      );
      setDetailOrganization(data.organization || organization);
      setDetailContracts(Array.isArray(data.contracts) ? data.contracts : []);
      setAdministrativeDraft({ ...ADMINISTRATIVE_DEFAULTS, ...(data.organization?.administrative || {}) });
    } catch (error) {
      setAlert({
        type: 'error',
        message: error?.response?.data?.message || `Erreur lors du chargement du détail pour ${organization.name}`,
      });
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeOrganizationDetails = () => {
    setDetailsDialogOpen(false);
    setDetailOrganization(null);
    setDetailContracts([]);
    setAdministrativeDraft(ADMINISTRATIVE_DEFAULTS);
  };

  const updateAdministrativeDraft = (field, value) => {
    setAdministrativeDraft((current) => ({ ...current, [field]: value }));
  };

  const saveAdministrativeDetails = async () => {
    if (!detailOrganization?._id) return;
    setDetailsSaving(true);
    try {
      const { data } = await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/organizations/${detailOrganization._id}/administrative`,
        administrativeDraft,
        { headers: getAuthHeaders() }
      );
      setDetailOrganization(data);
      dispatch(fetchOrganizations());
      setAlert({ type: 'success', message: `Informations administratives mises à jour pour ${data.name}` });
    } catch (error) {
      setAlert({
        type: 'error',
        message: error?.response?.data?.message || 'Erreur lors de la sauvegarde administrative.',
      });
    } finally {
      setDetailsSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: 'clients',
        columns: [
          {
            accessorFn: (row) => row.portfolioGroup || '',
            id: 'portfolioGroup',
            header: 'Groupe',
            size: 95,
            minSize: 75,
            maxSize: 130,
            Cell: ({ row }) => (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Chip
                  size="small"
                  variant={row.original.portfolioGroup ? 'filled' : 'outlined'}
                  color={row.original.portfolioGroup ? 'info' : 'default'}
                  label={row.original.portfolioGroup || '-'}
                  sx={{ maxWidth: 76 }}
                />
                <Tooltip title="Modifier le groupe">
                  <IconButton size="small" onClick={() => openPortfolioGroupDialog(row.original)}>
                    <EditIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ),
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => row.name,
            id: 'name',
            header: 'Nom',
            size: 260,
            Cell: ({ renderedCellValue }) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                {renderedCellValue}
              </Box>
            ),
            filterVariant: 'autocomplete',
          },
          {
            id: 'details',
            header: '',
            size: 55,
            enableColumnFilter: false,
            enableSorting: false,
            Cell: ({ row }) => (
              <Tooltip title="Voir le détail client">
                <IconButton size="small" color="primary" onClick={() => openOrganizationDetails(row.original)}>
                  <VisibilityRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ),
          },
          {
            accessorFn: (row) => normalizeStaffPortfolio(getOrganizationDisplayPortfolio(row))
              .map((entry) => getStaffLabel(entry.userDetails || staffById.get(String(entry.user)) || {}))
              .join(', '),
            id: 'accountPortfolio',
            header: 'Compte',
            size: 310,
            Cell: ({ row }) => (
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ flex: 1 }}>
                  {renderStaffPortfolio(getOrganizationDisplayPortfolio(row.original))}
                </Box>
                <Tooltip title="Modifier les comptes">
                  <IconButton size="small" onClick={() => openPortfolioDialog(row.original)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ),
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => row.parentOrganization?.name,
            id: 'parentOrganization',
            header: 'Parent Organization',
            size: 230,
            Cell: ({ renderedCellValue }) => renderedCellValue || '—',
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => getRootNames(row).join(', '),
            id: 'rootOrganization',
            header: 'Root Organization',
            size: 260,
            Cell: ({ row }) => renderRootsChips(row.original, 'table-root'),
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => row.pipedrive_id ? 'Oui' : 'Non',
            id: 'pipedrive',
            header: 'Pipedrive',
            size: 80,
            Cell: ({ row }) => row.original.pipedrive_id ? <DoneIcon color="success" /> : <ClearIcon color="error" />,
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => row.hudu_id ? 'Oui' : 'Non',
            id: 'hudu',
            header: 'Hudu',
            size: 110,
            Cell: ({ row }) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {row.original.hudu_id ? (
                  <DoneIcon color="success" />
                ) : (
                  <>
                    <ClearIcon color="error" />
                    {loading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <IconButton size="small" color="primary" onClick={() => createHudufromDb(row.original._id, row.original.name)}>
                        <AddIcon />
                      </IconButton>
                    )}
                  </>
                )}
              </Box>
            ),
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => row.infogerance ? 'Yes' : 'No',
            id: 'infogerance',
            header: 'Infogérance',
            size: 70,
            Cell: ({ row }) => row.original.infogerance ? <DoneIcon color="success" /> : <ClearIcon color="error" />,
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => row.o365Licenses && row.o365Licenses.length > 0 ? 'Yes' : 'No',
            id: 'o365Licenses',
            header: 'O365',
            size: 60,
            Cell: ({ row }) => row.original.o365Licenses && row.original.o365Licenses.length > 0 ? <DoneIcon color="success" /> : <ClearIcon color="error" />,
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => row.esetLicenses && row.esetLicenses.length > 0 ? 'Yes' : 'No',
            id: 'esetLicenses',
            header: 'ESET',
            size: 60,
            Cell: ({ row }) => row.original.esetLicenses && row.original.esetLicenses.length > 0 ? <DoneIcon color="success" /> : <ClearIcon color="error" />,
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => row.vbo365Licenses && row.vbo365Licenses.length > 0 ? 'Yes' : 'No',
            id: 'vbo365Licenses',
            header: 'Veeam Backup O365',
            size: 100,
            Cell: ({ row }) => row.original.vbo365Licenses && row.original.vbo365Licenses.length > 0 ? <DoneIcon color="success" /> : <ClearIcon color="error" />,
            filterVariant: 'autocomplete',
          },
        ],
      },
    ],
    [loading, staffById, openOrganizationDetails]
  );

  const table = useMaterialReactTable({
    localization: MRT_Localization_FR,
    enableGlobalFilter: true,
    enableColumnFilters: true,

    columns,
    data: filteredOrganizations || [],
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,

      density: 'compact',
      columnPinning: {
        left: ['mrt-row-expand', 'mrt-row-select'],
        right: ['mrt-row-actions'],
      },
    },
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [10, 20, 30],
      shape: 'rounded',
      variant: 'outlined',
    },
  });

  const treeSections = useMemo(() => {
    const treeMap = new Map(
      treeData.map((node) => [String(node.name || '').toUpperCase(), node])
    );

    return ROOT_TREE_SECTIONS.map((rootName) => ({
      rootName,
      node: treeMap.get(rootName) || null,
    }));
  }, [treeData]);

  const renderTree = (nodes, prefix = '') =>
    nodes.map((node) => {
      const label = (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', py: 0.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {node.isVirtualRoot ? <HubIcon fontSize="small" color="primary" /> : null}
              <Typography fontWeight={node.isVirtualRoot ? 700 : 500}>{node.name}</Typography>
              {node.infogerance ? (
                <Chip size="small" color="success" variant="outlined" label="Infogérance" />
              ) : null}
            </Stack>
            <Box sx={{ mt: 0.75 }}>
              {renderRootsChips(node, `tree-root-${node._id}`)}
            </Box>
            <Box sx={{ mt: 0.75 }}>
              {renderTypeChips(node, `tree-type-${node._id}`)}
            </Box>
          </Box>

          {node.relatedNodes.length > 0 ? (
            <Stack direction="column" spacing={1} sx={{ ml: 4 }}>
              {node.relatedNodes.map((rel) => (
                <Box
                  key={`${rel._id}-related`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 0.5,
                    borderRadius: 1,
                    bgcolor: 'rgba(33, 150, 243, 0.1)',
                  }}
                >
                  <Chip label="Related" size="small" color="info" />
                  <Typography variant="body2">{rel.name}</Typography>
                </Box>
              ))}
            </Stack>
          ) : null}
        </Box>
      );

      return (
        <TreeItem
          key={`${prefix}${node._id}`}
          itemId={`${prefix}${node._id}`}
          label={label}
        >
          {node.children.length > 0 ? renderTree(node.children, `${prefix}${node._id}-`) : null}
        </TreeItem>
      );
    });

  const RefreshPipeHudu = async () => {
    try {
      setLoadingRefreshFromPipeHudu(true);
      const syncPipedrive = await axios.get(`${process.env.REACT_APP_API_URL}/api/organizations/syncpipedrive`);

      if (syncPipedrive.status === 200) {
        const GetHuduOrg = await axios.get(`${process.env.REACT_APP_API_URL}/api/organizations/setOrganizationToHudu`);

        if (GetHuduOrg.status === 200) {
          const deduplicate = await axios.get(`${process.env.REACT_APP_API_URL}/api/organizations/deduplicate`);

          if (deduplicate.status === 200) {
            dispatch(fetchOrganizations());
            window.location.reload();
          }
        }
      }
    } catch (error) {
      console.error('Error fetching URLs:', error);
      setAlert({
        type: 'error',
        message: 'Erreur pendant la synchronisation Pipedrive/Hudu',
      });
    } finally {
      setLoadingRefreshFromPipeHudu(false);
    }
  };

  return (
    <Box>
      {filteredOrganizations && filteredOrganizations.length > 0 ? (
        <Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ my: 1 }}>
            <Typography variant="h6">
              Clients
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={organizationSyncLoading || organizationSyncStatus?.running ? <CircularProgress size={14} /> : <SyncIcon />}
              onClick={startOrganizationSync}
              disabled={organizationSyncLoading || organizationSyncStatus?.running}
            >
              Synchroniser clients
            </Button>
          </Stack>

          {(organizationSyncStatus || organizationSyncError) ? (
            <Alert
              severity={organizationSyncError ? 'error' : organizationSyncStatus?.running ? 'info' : organizationSyncStatus?.status === 'error' ? 'error' : 'success'}
              sx={{ mb: 2 }}
            >
              <Stack spacing={0.25}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {organizationSyncError || organizationSyncStatus?.message || 'Synchronisation clients prête.'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {organizationSyncStatus?.running
                    ? `Lancé le ${formatDateTime(organizationSyncStatus.startedAt)}. Cette synchronisation peut prendre plusieurs minutes, merci de patienter.`
                    : `Dernier lancement : ${formatDateTime(organizationSyncStatus?.startedAt || organizationSyncStatus?.lastRunAt)} · Fin : ${formatDateTime(organizationSyncStatus?.finishedAt)}`}
                </Typography>
              </Stack>
            </Alert>
          ) : null}


          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2} alignContent="center">
              {treeSections.map(({ rootName, node }) => (
                <Grid key={rootName} size={{ xs: 12, md: 4 }}>
                  <Card sx={{ p: 1, mt: 2, borderRadius: 3, height: '100%' }}>
                    <Box display="flex" alignItems="center" my={1} mx={2} columnGap={1}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <DeviceHubIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6">{rootName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Organigramme root organization
                        </Typography>
                      </Box>
                    </Box>
                    {node ? (
                      <SimpleTreeView
                        slots={{
                          expandIcon: ChevronRight,
                          collapseIcon: ExpandMore,
                        }}
                      >
                        {renderTree([node], `${rootName}-`)}
                      </SimpleTreeView>
                    ) : (
                      <Box sx={{ px: 2, pb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Aucune organisation trouvée pour cette root.
                        </Typography>
                      </Box>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box>
            <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
              Liste des clients
            </Typography>

            {loadingRefreshFromPipeHudu ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <CircularProgress size={30} />
              </Box>
            ) : null}

            {alert ? (
              <Box sx={{ mb: 2 }}>
                <Alert variant="filled" severity={alert.type === 'success' ? 'success' : 'error'}>
                  {alert.message}
                </Alert>
              </Box>
            ) : null}

            <MaterialReactTable table={table} />

            <Dialog open={portfolioDialogOpen} onClose={closePortfolioDialog} fullWidth maxWidth="sm">
              <DialogTitle>
                {`Portefeuille client - ${editingPortfolioOrg?.name || ''}`}
              </DialogTitle>
              <DialogContent dividers>
                <Autocomplete
                  multiple
                  options={staffOptions}
                  value={portfolioDraftUsers}
                  getOptionLabel={(option) => option.label || getStaffLabel(option)}
                  isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
                  onChange={(_, value) => setPortfolioDraftUsers(value)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.value}
                        size="small"
                        label={`${index + 1}. ${option.label}`}
                      />
                    ))
                  }
                  renderInput={(params) => <TextField {...params} label="Gestionnaires" />}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={closePortfolioDialog}>Annuler</Button>
                <Button variant="contained" onClick={savePortfolio} disabled={portfolioSaving}>
                  Enregistrer
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog open={portfolioGroupDialogOpen} onClose={closePortfolioGroupDialog} fullWidth maxWidth="xs">
              <DialogTitle>
                {`Groupe - ${editingPortfolioGroupOrg?.name || ''}`}
              </DialogTitle>
              <DialogContent dividers>
                <TextField
                  autoFocus
                  fullWidth
                  label="Groupe"
                  value={portfolioGroupDraft}
                  onChange={(event) => setPortfolioGroupDraft(event.target.value)}
                  helperText="Exemple : CBO, HOLD INVEST, MRN"
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={closePortfolioGroupDialog}>Annuler</Button>
                <Button variant="contained" onClick={savePortfolioGroup} disabled={portfolioGroupSaving}>
                  Enregistrer
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog open={detailsDialogOpen} onClose={closeOrganizationDetails} fullWidth maxWidth="lg">
              <DialogTitle>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Box>
                    <Typography variant="h6">{detailOrganization?.name || 'Détail client'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Informations administratives et techniques
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={detailsSaving ? <CircularProgress size={14} /> : <SaveIcon />}
                    onClick={saveAdministrativeDetails}
                    disabled={detailsSaving || detailsLoading || !detailOrganization?._id}
                  >
                    Enregistrer
                  </Button>
                </Stack>
              </DialogTitle>
              <DialogContent dividers sx={{ p: 0 }}>
                <Tabs value={detailsTab} onChange={(_, value) => setDetailsTab(value)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Tab value="administrative" label="Administrative" />
                  <Tab value="technical" label="Technique" />
                </Tabs>

                {detailsLoading ? (
                  <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : null}

                {!detailsLoading && detailsTab === 'administrative' ? (
                  <Box sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      {Object.entries(ADMINISTRATIVE_LABELS).map(([field, label]) => (
                        <Grid key={field} size={{ xs: 12, sm: 6, md: 4 }}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label={label}
                            value={administrativeDraft[field] || ''}
                            onChange={(event) => updateAdministrativeDraft(field, event.target.value)}
                          >
                            <MenuItem value="">Non renseigné</MenuItem>
                            {(ADMINISTRATIVE_SELECTS[field] || []).map((option) => (
                              <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      ))}
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    <Stack spacing={1.5}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Supports EasyRedmine rattachés aux CRT</Typography>
                        <Typography variant="body2" color="text.secondary">Supports configurés dans les contrats liés au client.</Typography>
                      </Box>
                      {detailContracts.length === 0 ? (
                        <Alert severity="info">Aucun contrat rattaché à ce client.</Alert>
                      ) : (
                        <Stack spacing={1}>
                          {detailContracts.map((contract) => (
                            <Card key={contract._id || getContractTitle(contract)} variant="outlined" sx={{ p: 1.5 }}>
                              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{getContractTitle(contract)}</Typography>
                                  <Typography variant="caption" color="text.secondary">{getContractSubtitle(contract) || 'Contrat'}</Typography>
                                </Box>
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                  <Chip size="small" label={contract.billingFrequency || 'Fréquence -'} />
                                  <Chip size="small" label={contract.paymentType || 'Règlement -'} />
                                  {(contract.support || []).length === 0 ? (
                                    <Chip size="small" variant="outlined" label="Aucun support" />
                                  ) : (contract.support || []).map((support) => (
                                    <Chip
                                      key={`${contract._id}-${support.projectId || support.projectName}`}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      label={support.projectName || support.projectId}
                                    />
                                  ))}
                                </Stack>
                              </Stack>
                            </Card>
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  </Box>
                ) : null}

                {!detailsLoading && detailsTab === 'technical' ? (
                  <Box sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      {[
                        ['Hudu ID', detailOrganization?.hudu_id || '-'],
                        ['Pipedrive ID', detailOrganization?.pipedrive_id || '-'],
                        ['ITop ID', detailOrganization?.itop_id || '-'],
                        ['Root', getRootNames(detailOrganization).join(', ') || '-'],
                        ['Parent', detailOrganization?.parentOrganization?.name || '-'],
                        ['Adresse', detailOrganization?.address?.value || '-'],
                        ['O365 licences', detailOrganization?.o365Licenses?.length || 0],
                        ['ESET licences', detailOrganization?.esetLicenses?.length || 0],
                        ['Veeam Backup O365', detailOrganization?.vbo365Licenses?.length || 0],
                      ].map(([label, value]) => (
                        <Grid key={label} size={{ xs: 12, sm: 6, md: 4 }}>
                          <Card variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                            <Typography variant="caption" color="text.secondary">{label}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5 }}>{value}</Typography>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ) : null}
              </DialogContent>
              <DialogActions>
                <Button onClick={closeOrganizationDetails}>Fermer</Button>
              </DialogActions>
            </Dialog>

          </Box>
        </Box>
      ) : (
        <Box>
          <Skeleton variant="text" width={260} sx={{ fontSize: '3rem' }} />
          <Skeleton variant="text" width={200} sx={{ fontSize: '2rem' }} />
          <Grid container spacing={2} alignContent="center">
            <Grid size={{ xs: 6, md: 4 }}>
              <Skeleton variant="rectangular" width="100%" height={200} />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <Skeleton variant="rectangular" width="100%" height={200} />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <Skeleton variant="rectangular" width="100%" height={200} />
            </Grid>
          </Grid>
          <Skeleton variant="text" width={200} sx={{ fontSize: '2rem' }} />
          <Skeleton variant="rectangular" width="100%" height={600} />
        </Box>
      )}
    </Box>
  );
};

export default Organizations;
